import React from 'react'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { accentUtils, from, t } from '../../services/HelperService'
import { queryNoTracking } from '../../services/DataService'
import { AccentDateUnbound } from '../unbound/AccentDateUnbound'
import { AccentCheckUnbound } from '../unbound/AccentCheckUnbound'
import { AccentNumberUnbound } from '../unbound/AccentNumberUnbound'
import { AccentTextUnbound } from '../unbound/AccentTextUnBound'
import { FilterDropdown } from '../FilterButtons'
import { findSchemaItem, filterTypes, filterOps, parseFilterPredicate, changeFilterPredicateValue, isOrderedJobLineStageField, isJobLineStageFilterOp } from './AdvancedFilterConstants'
import { getQueryProductList } from './InsyteQueryProductOptionService'



const query_AdvancedQuery_LookupByType = queryNoTracking('AdvancedQuery_LookupByType', 0);
const query_AdvancedQuery_Lookup = queryNoTracking('AdvancedQuery_Lookup', 0);

const OrderedStageSingleSelect = React.memo(props => {
    const data = React.useMemo(
        () => from(props.data ?? []).select(i => ({ ...i, Text: t(i.Text) })).toArray(),
        [props.data]
    );

    const selectedValue = from(data).firstOrDefault(i => i.ID === props.value) ?? null;

    return <DropDownList
        data={data}
        value={selectedValue}
        textField="Text"
        dataItemKey="ID"
        onChange={e => props.onChange(e.value?.ID ?? null)}
        disabled={props.readOnly}
        style={{ width: "100%" }}
    />;
});


export const InsyteQueryValueCtrl = React.memo(props => {


    const opInfo = parseFilterPredicate(props.item);

    const schemaItem = findSchemaItem(props.schema, opInfo.field);

    const type = accentUtils.isNull(schemaItem) ? "text" : schemaItem.Type;


    const onChange = React.useCallback(e => {
        changeFilterPredicateValue(props.item, e);
        props.onItemChanged(true);
    }, [props]);

    if (opInfo.op === filterOps.isempty || opInfo.op === filterOps.isnotempty) return null;

    switch (type) {
        case filterTypes.select:
            if (isOrderedJobLineStageField(opInfo.field) && isJobLineStageFilterOp(opInfo.op))
                return <InsyteQueryValueJobLineStageCtrl key={`${opInfo.field}_${props.supplierKey ?? 'all'}_${opInfo.op}`} itemInfo={opInfo} schemaItem={schemaItem} onChange={onChange} readOnly={props.readOnly} supplierKey={props.supplierKey}/>;

            return <InsyteQueryValueSelectCtrl key={`${opInfo.field}_${props.supplierKey ?? 'all'}`} itemInfo={opInfo} schemaItem={schemaItem} onChange={onChange} readOnly={ props.readOnly} supplierKey={props.supplierKey}/>;
        case filterTypes.check: return <AccentCheckUnbound tagName={opInfo.field + "_chk"} defaultValue={opInfo.arg} onChange={e => onChange(e.checked)} disabled={props.readOnly}/>;
        case filterTypes.date:
            if (opInfo.op === filterOps.relative)
                return <InsyteQueryValueRelativeCtrl itemInfo={opInfo} onChange={onChange} readOnly={props.readOnly}/>;
            else if (opInfo.op === filterOps.within)
                return <AccentNumberUnbound defaultValue={opInfo.arg} onChange={onChange} isInt disabled={props.readOnly}/>;
            else
                return <AccentDateUnbound defaultValue={opInfo.arg} onChange={onChange} disabled={props.readOnly}/>;
        case filterTypes.numeric: return <AccentNumberUnbound defaultValue={opInfo.arg} onChange={onChange} disabled={props.readOnly}/>;
        default: return <AccentTextUnbound defaultValue={opInfo.arg} onChange={onChange} disabled={props.readOnly} />;
    }
});

export const InsyteQueryValueJobLineStageCtrl = React.memo(props => {
    const normalizeValue = React.useCallback(value => {
        return Array.isArray(value) ? { stages: value } : (accentUtils.isNull(value) ? {} : value);
    }, []);

    const queryLookup = React.useCallback(() => {
        if (!accentUtils.isEmpty(props.schemaItem.LookupKeyType))
            return query_AdvancedQuery_LookupByType.getAllWithCache({ lookupKey: props.schemaItem.LookupKeyType });

        return query_AdvancedQuery_Lookup.getAllWithCache({ lookupKey: props.schemaItem.LookupKey });
    }, [props.schemaItem]);

    const [currentValue, setCurrentValue] = React.useState(() => normalizeValue(props.itemInfo.arg));
    const [lookupData, setLookupData] = React.useState(null);

    React.useEffect(() => {
        setCurrentValue(normalizeValue(props.itemInfo.arg));
    }, [props.itemInfo.arg, normalizeValue]);

    React.useEffect(() => {
        let cancelled = false;

        queryLookup().then(data => {
            if (!cancelled) {
                setLookupData(data);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [queryLookup]);

    const stages = React.useMemo(
        () => Array.isArray(currentValue.stages) ? currentValue.stages : [],
        [currentValue]
    );
    const existingValue = from(stages).select(v => { return { ID: v } }).toArray();
    const isComparison = props.itemInfo.op !== filterOps.allin && props.itemInfo.op !== filterOps.nonein && props.itemInfo.op !== filterOps.exactlyin;

    const emit = React.useCallback(changes => {
        const nextValue = {
            stages: stages,
            excludeServices: currentValue.excludeServices === true,
            excludeCharges: currentValue.excludeCharges === true,
            ...changes
        };

        setCurrentValue(nextValue);
        props.onChange(nextValue);
    }, [props, stages, currentValue]);

    const onStagesChanged = React.useCallback(e => {
        const selectedStages = from(e.value).select(i => i.ID).toArray();
        emit({ stages: isComparison ? selectedStages.slice(0, 1) : selectedStages });
    }, [emit, isComparison]);

    const onSingleStageChanged = React.useCallback(stage => {
        emit({ stages: accentUtils.isNull(stage) ? [] : [stage] });
    }, [emit]);

    return <div className="acc-job-line-stage-filter">
        {isComparison
            ? <OrderedStageSingleSelect data={lookupData ?? []} value={stages[0]} onChange={onSingleStageChanged} readOnly={props.readOnly} />
            : <FilterDropdown valueField="ID" textField="Text" dataSource={queryLookup} onChange={onStagesChanged} translate selectAllOnLoad={false} value={existingValue} readOnly={props.readOnly} />
        }
        <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
            <AccentCheckUnbound
                tagName="excludeServices"
                defaultValue={currentValue.excludeServices === true}
                onChange={e => emit({ excludeServices: e.checked })}
                label="application_strings.views.filterView.excludeServices"
                disabled={props.readOnly}
            />
            <AccentCheckUnbound
                tagName="excludeCharges"
                defaultValue={currentValue.excludeCharges === true}
                onChange={e => emit({ excludeCharges: e.checked })}
                label="application_strings.views.filterView.excludeCharges"
                disabled={props.readOnly}
            />
        </div>
    </div>;
});

export const InsyteQueryValueSelectCtrl = React.memo(props => {

    const queryLookup = React.useCallback(() => {
        if (`${props.schemaItem.LookupKey ?? ''}`.toLowerCase() === 'product' && props.supplierKey) {
            return getQueryProductList({ supplierKey: props.supplierKey })
                .then(products => products.map(p => ({ ID: p.ID, Text: p.Name })));
        }

        if (!accentUtils.isEmpty(props.schemaItem.LookupKeyType))
            return query_AdvancedQuery_LookupByType.getAllWithCache({lookupKey: props.schemaItem.LookupKeyType });
            
        return query_AdvancedQuery_Lookup.getAllWithCache({ lookupKey: props.schemaItem.LookupKey });
    }, [props.schemaItem, props.supplierKey]);

    const onChange = React.useCallback(e => {

        const selection = from(e.value).select(i => i.ID).toArray();

        props.onChange(selection);

    }, [props]);

    const existingValue = accentUtils.isNull(props.itemInfo.arg) ? [] : from(props.itemInfo.arg).select(v => { return { ID : v}}).toArray();


    return <FilterDropdown valueField="ID" textField="Text" dataSource={queryLookup} onChange={onChange} translate selectAllOnLoad={false} value={existingValue} readOnly={props.readOnly}/>;
});


export const InsyteQueryValueRelativeCtrl = React.memo(props => {

    

    const [start, setStart] = React.useState(accentUtils.isNull(props.itemInfo.arg) ? 0 : props.itemInfo.arg[0]);
    const [end, setEnd] = React.useState(accentUtils.isNull(props.itemInfo.arg) ? 0 : props.itemInfo.arg[1]);

    const onChange = React.useCallback((e, t) => {

        
        var res = [0,0];

        if (t === "start") {            
            res = [accentUtils.isNull(e) ? start : Number(e), end];
            setStart(s=> res[0]);
        } else {
            res = [start, accentUtils.isNull(e) ? end: Number(e)];
            setEnd(e => res[1]);
        }

        props.onChange(res);

    }, [props, start, end, setStart, setEnd]);

    
    return <div style={{ display: "flex" }}>
        <AccentNumberUnbound defaultValue={start} onChange={e => onChange(e, "start")} label="Start" isInt disabled={props.readOnly}/>
        <AccentNumberUnbound defaultValue={end} onChange={e => onChange(e, "end")} label="End" isInt disabled={props.readOnly}/>
    </div>;
});